import { Config } from "@app/config";
import {
	faCakeCandles as fasCakeCandles,
	faCalendar as fasCalendar,
	faPlay as fasPlay,
} from "@awesome.me/kit-3777c2145e/icons/classic/solid";
import { AbstractStep } from "@component/assets/AbstractLeadForm/Steps/AbstractStep";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import bankmonitorImage from "@img/logos/bankmonitor-white.svg";
import { ButtonLegacy, FormCurrency, FormNumber } from "@shared/ui";
import { formatText } from "@shared/ui/format";
import dayjs from "dayjs";
import type { ReactNode } from "react";
import { Card } from "react-bootstrap";

export interface StepFields {
	birthYear: number;
	durationYears: number;
	collateralAmount: number;
}

const MIN_AGE = 16;
const MAX_AGE = 64;
const MAX_INSURED_AGE = 80;

const MIN_DURATION_YEARS = 5;
const MAX_DURATION_YEARS = 25;

const MIN_RISK_INSURANCE_AMOUNT = 5_000_000;
const MAX_RISK_INSURANCE_AMOUNT = 100_000_000;

export class Step1 extends AbstractStep<StepFields> {
	protected initValues(): StepFields {
		const birthYear = Config.getCurrentUser().getBirthYear();

		return {
			birthYear,
			durationYears: undefined,
			collateralAmount: undefined,
		};
	}

	protected renderContent(): ReactNode {
		return (
			<>
				<Card.Header className="bg-secondary-gradient text-center text-white">
					<img
						className="my-1"
						src={bankmonitorImage}
						width="160"
						height="25"
						alt="Bankmonitor"
						loading="eager"
					/>
					<hr />
					<div className="h2 mb-0 text-uppercase">
						Kiszámoljuk <strong className="text-nowrap font-weight-black">mennyibe kerül</strong>
						<br />a családod védelme
					</div>
					<hr />
				</Card.Header>
				<Card.Body className="d-flex flex-column">
					<div className="h4 text-uppercase text-center my-3">Hitelfedezetre kínálunk megoldást!</div>

					<FormNumber
						label="Melyik évben születtél?"
						name="birthYear"
						maxLength={4}
						prepend={<FontAwesomeIcon icon={fasCakeCandles} />}
						validate={this.validBirthYear}
						required
					/>
					<FormNumber
						label="Hány évig van szükséged védelemre?"
						name="durationYears"
						prepend={<FontAwesomeIcon icon={fasCalendar} />}
						infoTooltip="Ennyi időn belül bekövetkező váratlan esemény esetén fizet a biztosító."
						append="év"
						validate={this.validDurationYears}
						required
					/>
					<FormCurrency
						label="Milyen összegű biztosítási védelmet szeretnél? (minimum 5 millió Ft)"
						name="collateralAmount"
						infoTooltip="Az a maximális kártérítési biztosítási összeg, amit kritikus betegség, ill. rokkantság esetén kifizet a biztosító a biztosítottnak, vagy haláseset okán a biztosító kifizet a kedvezményezett(ek) részére."
						validate={this.validcollateralAmount}
						required
					/>
				</Card.Body>
				<Card.Footer className="text-center">
					<ButtonLegacy type="submit" icon={<FontAwesomeIcon icon={fasPlay} />} className="text-uppercase">
						Kalkuláció indítása
					</ButtonLegacy>
				</Card.Footer>
			</>
		);
	}

	private validBirthYear(birthYear: number): string {
		const actualYear = dayjs().year();

		if (birthYear < 1000) {
			return "Add meg a teljes évszámot!";
		}

		if (birthYear > actualYear) {
			return `A születési éve nem lehet nagyobb mint ${actualYear}`;
		}

		if (!/^[0-9]{4}$/.test(birthYear.toString())) {
			return "Hibás évszám!";
		}

		const age = dayjs().diff(dayjs(`${birthYear}-01-01`), "years");

		if (age < MIN_AGE) {
			return `${MIN_AGE} év alatt sajnos nem tudunk önálló biztosítási konstrukciót ajánlani.`;
		}

		if (age > MAX_AGE) {
			return `${MAX_AGE} évnél idősebb személy nem veheti igénybe a biztosítást!`;
		}
	}

	private validDurationYears(durationYears: number, values: Record<string, any>): string {
		if (durationYears < MIN_DURATION_YEARS) {
			return `A védelem hossza nem lehet kevesebb mint ${MIN_DURATION_YEARS} év!`;
		}

		if (durationYears > MAX_DURATION_YEARS) {
			return `A védelem hossza nem lehet több mint ${MAX_DURATION_YEARS} év!`;
		}

		const { birthYear = 0 } = values;

		if (birthYear) {
			const age = dayjs().diff(dayjs(`${birthYear}-01-01`), "years");

			if (age + durationYears > MAX_INSURED_AGE) {
				return `A szerződés lejáratakor a biztosított legfeljebb ${MAX_INSURED_AGE} éves lehet. Kérjük rövidítsd a tartamot!`;
			}
		}
	}

	private validcollateralAmount(collateralAmount: number): string {
		if (collateralAmount < MIN_RISK_INSURANCE_AMOUNT) {
			return `Kalkulátorunkban a minimális biztosítási összeg ${formatText(
				MIN_RISK_INSURANCE_AMOUNT,
				"ft",
			)}. Növeld a biztosítási összeget, vagy add meg az email címedet, hogy megtalálhassunk a számodra megfelelő, személyre szabott megoldást.`;
		}

		if (collateralAmount > MAX_RISK_INSURANCE_AMOUNT) {
			return `Kalkulátorunkban a maximális biztosítási összeg ${formatText(
				MAX_RISK_INSURANCE_AMOUNT,
				"ft",
			)}. Csökkentsd a biztosítási összeget, vagy add meg az email címedet, hogy megtalálhassunk a számodra megfelelő, személyre szabott megoldást.`;
		}
	}

	protected getComponentName(): string {
		return "Step1";
	}
}
